import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { actividadService, ActividadCreate } from '../../services/actividadService';
import { useAuthStore } from '../../store/authStore';

const ActividadForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // Obtener parámetros de la URL si vienen de un lead/oportunidad/cliente
  const relacionTipo = searchParams.get('relacionTipo') as 'lead' | 'cliente' | 'oportunidad' | null;
  const relacionId = searchParams.get('relacionId');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<ActividadCreate>({
    defaultValues: {
      tipo: 'llamada',
      relacionTipo: relacionTipo || 'lead',
      relacionId: relacionId ? Number(relacionId) : undefined,
      estado: 'pendiente',
      usuarioAsignadoId: user?.id
    }
  });

  const tipo = watch('tipo');
  const relacionTipoForm = watch('relacionTipo');

  const createMutation = useMutation(actividadService.create, {
    onSuccess: () => {
      toast.success('Actividad creada correctamente');
      queryClient.invalidateQueries('actividades');
      queryClient.invalidateQueries(['actividades', relacionTipoForm]);
      
      // Si venía de una relación específica, volver ahí
      if (relacionTipo && relacionId) {
        if (relacionTipo === 'lead') {
          navigate(`/crm/leads/${relacionId}`);
        } else if (relacionTipo === 'oportunidad') {
          navigate(`/crm/oportunidades/${relacionId}`);
        } else {
          navigate(`/clientes/${relacionId}`);
        }
      } else {
        navigate('/crm/actividades');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear actividad');
    }
  });

  const onSubmit = (data: any) => {
    // Si no hay relacionId, no se puede crear
    if (!data.relacionId) {
      toast.error('Debe seleccionar un lead, cliente u oportunidad');
      return;
    }

    createMutation.mutate(data);
  };

  const tiposActividad = [
    { value: 'llamada', label: 'Llamada', icon: '📞' },
    { value: 'visita', label: 'Visita', icon: '🚶' },
    { value: 'correo', label: 'Correo', icon: '📧' },
    { value: 'whatsapp', label: 'WhatsApp', icon: '💬' },
    { value: 'reunion', label: 'Reunión', icon: '🤝' },
    { value: 'tarea', label: 'Tarea', icon: '✓' },
    { value: 'nota', label: 'Nota', icon: '📝' }
  ];

  const resultadosComunes = {
    llamada: ['Contestó', 'No contestó', 'Ocupado', 'Dejó mensaje', 'Llamará después'],
    visita: ['Atendido', 'No estaba', 'Reagendó', 'Canceló'],
    correo: ['Enviado', 'Leído', 'Respondido', 'Rebotado'],
    whatsapp: ['Enviado', 'Leído', 'Respondido', 'No respondió'],
    reunion: ['Realizada', 'Cancelada', 'Reagendada'],
    tarea: ['Completada', 'Pendiente', 'Cancelada'],
    nota: []
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={() => {
              if (relacionTipo && relacionId) {
                if (relacionTipo === 'lead') {
                  navigate(`/crm/leads/${relacionId}`);
                } else if (relacionTipo === 'oportunidad') {
                  navigate(`/crm/oportunidades/${relacionId}`);
                } else {
                  navigate(`/clientes/${relacionId}`);
                }
              } else {
                navigate('/crm/actividades');
              }
            }}
            className="text-gray-600 hover:text-gray-800 mb-2"
          >
            ← Volver
          </button>
          <h1 className="text-3xl font-bold text-gray-800">Nueva Actividad</h1>
          <p className="text-gray-600 mt-1">Registra el seguimiento de tu contacto</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Tipo de Actividad */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Tipo de Actividad</h2>
            <div className="grid grid-cols-4 gap-3">
              {tiposActividad.map((tipoAct) => (
                <label
                  key={tipoAct.value}
                  className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition ${
                    tipo === tipoAct.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    {...register('tipo', { required: 'Tipo de actividad es requerido' })}
                    value={tipoAct.value}
                    className="hidden"
                  />
                  <span className="text-2xl mb-2">{tipoAct.icon}</span>
                  <span className="text-sm font-medium">{tipoAct.label}</span>
                </label>
              ))}
            </div>
            {errors.tipo && (
              <p className="text-red-500 text-sm mt-2">{errors.tipo.message}</p>
            )}
          </div>

          {/* Relación */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Relacionado con</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Relación *
                </label>
                <select
                  {...register('relacionTipo', { required: 'Tipo de relación es requerido' })}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.relacionTipo ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                >
                  <option value="lead">Lead</option>
                  <option value="cliente">Cliente</option>
                  <option value="oportunidad">Oportunidad</option>
                </select>
                {errors.relacionTipo && (
                  <p className="text-red-500 text-sm mt-1">{errors.relacionTipo.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID de la Relación *
                </label>
                <input
                  type="number"
                  {...register('relacionId', { 
                    required: 'ID de relación es requerido',
                    valueAsNumber: true,
                    min: { value: 1, message: 'ID debe ser válido' }
                  })}
                  placeholder="ID del lead/cliente/oportunidad"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.relacionId ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.relacionId && (
                  <p className="text-red-500 text-sm mt-1">{errors.relacionId.message}</p>
                )}
                {relacionId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Pre-cargado desde: {relacionTipo} #{relacionId}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Información de la Actividad */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Información de la Actividad</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <input
                  {...register('titulo', { required: 'Título es requerido' })}
                  placeholder="Ej: Llamada de seguimiento"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.titulo ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'
                  }`}
                />
                {errors.titulo && (
                  <p className="text-red-500 text-sm mt-1">{errors.titulo.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  {...register('descripcion')}
                  rows={4}
                  placeholder="Detalles de la actividad..."
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha y Hora de Inicio
                  </label>
                  <input
                    type="datetime-local"
                    {...register('fechaInicio')}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha y Hora de Fin
                  </label>
                  <input
                    type="datetime-local"
                    {...register('fechaFin')}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              {(tipo === 'llamada' || tipo === 'visita' || tipo === 'reunion') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duración (minutos)
                  </label>
                  <input
                    type="number"
                    min="0"
                    {...register('duracionMinutos', { valueAsNumber: true })}
                    placeholder="Ej: 30"
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}
              {resultadosComunes[tipo as keyof typeof resultadosComunes]?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Resultado
                  </label>
                  <select
                    {...register('resultado')}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Selecciona resultado</option>
                    {resultadosComunes[tipo as keyof typeof resultadosComunes].map((resultado) => (
                      <option key={resultado} value={resultado}>
                        {resultado}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  {...register('estado')}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en_proceso">En Proceso</option>
                  <option value="completada">Completada</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas Adicionales
                </label>
                <textarea
                  {...register('notas')}
                  rows={3}
                  placeholder="Notas adicionales sobre la actividad..."
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => {
                if (relacionTipo && relacionId) {
                  if (relacionTipo === 'lead') {
                    navigate(`/crm/leads/${relacionId}`);
                  } else if (relacionTipo === 'oportunidad') {
                    navigate(`/crm/oportunidades/${relacionId}`);
                  } else {
                    navigate(`/clientes/${relacionId}`);
                  }
                } else {
                  navigate('/crm/actividades');
                }
              }}
              className="px-6 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading ? 'Guardando...' : 'Crear Actividad'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActividadForm;

